import { RegisteredDetails } from "../components/RegisteredDetails";
import { RegisterLayout } from "../components/RegisterLayout";
import { Truck, Ship, Timer, PackageCheck, MapPin } from "lucide-react";

const LANES = [
  { Icon: Truck, mode: "Road (FTL / PTL)", window: "3 – 7 working days", note: "Regional & inter-state bulk movement by dedicated trucks and market trailers." },
  { Icon: Ship, mode: "Rail & coastal", window: "7 – 15 working days", note: "High-tonnage cement & steel over long distances via rail rakes and coastal shipping." },
  { Icon: PackageCheck, mode: "Project consignment", window: "Scheduled to site programme", note: "Phased, milestone-linked deliveries aligned to your construction schedule." },
];

const Section = ({ n, title, children }) => (
  <section className="py-8 border-b border-white/10" data-testid={`delivery-section-${n}`}>
    <div className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10">
      <span className="font-serif text-3xl text-[#B87333]/40">{n}</span>
      <div>
        <h2 className="font-serif text-2xl md:text-3xl mb-4">{title}</h2>
        <div className="font-body text-[#C9C9C2] leading-relaxed space-y-3 max-w-2xl">{children}</div>
      </div>
    </div>
  </section>
);

export default function Delivery() {
  return (
    <RegisterLayout
      kicker="/ Logistics"
      title="Shipping & delivery timelines"
      subtitle="Vinci Inc. moves bulk cement, steel and allied materials PAN-India. Indicative lead times by lane are below — every consignment is confirmed against live stock, quantity and destination at the time of order."
    >
      <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden mb-14" data-testid="delivery-lanes">
        {LANES.map((l) => (
          <div key={l.mode} className="bg-[#070707] p-7">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black"><l.Icon size={18} /></span>
            <div className="font-serif text-xl mt-5">{l.mode}</div>
            <div className="flex items-center gap-2 mt-2 text-[#2FB999]">
              <Timer size={14} />
              <span className="font-grotesk text-[11px] uppercase tracking-[0.16em]">{l.window}</span>
            </div>
            <p className="font-body text-sm text-[#8A8A82] mt-3 leading-relaxed">{l.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-2">
        <Section n="01" title="How lead times are set">
          <p>Timelines begin from <strong className="text-[#F5F5F0]">confirmed payment</strong> and stock allocation. Actual dispatch depends on material, grade, order quantity (MOQ) and destination State. Your order summary and confirmation email carry the applicable window.</p>
        </Section>
        <Section n="02" title="Dispatch & tracking">
          <p>Once dispatched, a courier / LR / AWB number is shared by email and appears against your order in your account. Large project consignments may move in phased lots, each tracked separately.</p>
        </Section>
        <Section n="03" title="PAN-India coverage">
          <p className="flex items-start gap-2"><MapPin size={16} className="text-[#B87333] mt-1 shrink-0" /> We deliver across all States and Union Territories through our group of State-wise entities, ensuring compliant billing and last-mile reach even for smaller regions.</p>
        </Section>
        <Section n="04" title="Delays & force majeure">
          <p>Estimates are indicative, not guaranteed. Weather, transporter strikes, statutory checks, and other events beyond our control may affect timelines. We proactively update you and re-plan dispatch where required.</p>
        </Section>
        <Section n="05" title="Receiving your consignment">
          <p>Please inspect goods on delivery and note any visible damage or shortage on the receipt before signing. Report discrepancies to our trade desk immediately so we can resolve them quickly. See our Refund Policy for eligibility.</p>
        </Section>
        <Section n="06" title="Contact & registered details">
          <RegisteredDetails />
        </Section>
      </div>
    </RegisterLayout>
  );
}
